/**
 * SendTrackingModal.jsx
 * 
 * ✅ REUSABLE COMPONENT - Sabhi roles ke liye use kar sakte ho
 * Sirf `role` prop change karo:
 *   - role="BackToProjectIncharge"   → Samanvay se Project Incharge ko bhejne ke liye
 *   - role="Collector"               → Samanvay se Collector ko bhejne ke liye
 *   - role="Samanvay"                → Project Incharge se Samanvay ko bhejne ke liye
 *   - role="LAO"                     → Collector se LAO ko bhejne ke liye
 * 
 * Props:
 *   isOpen         - boolean: modal show/hide
 *   onClose        - fn: modal band karne ke liye
 *   role           - string: upar diye gaye roles mein se ek
 *   projectId      - current project id
 *   proposalId     - current proposal id
 *   apiService     - imported apiService
 *   translations   - language translations object
 *   language       - current language
 *   onSuccess      - fn: successful send ke baad call hoga (refresh buttons etc.)
 */

import React, { useState, useEffect } from 'react';
import { Send, Loader2, Eye, X } from 'lucide-react';
import { useSignature } from '../DigitalSignature/UseSignatue';
import SignaturePad from '../DigitalSignature/SignaturePad';
import apiService from '../config';
// ─────────────────────────────────────────────
// Role Configuration — yahan sirf role add karo
// ─────────────────────────────────────────────
const ROLE_CONFIG = {

    // ─── Legacy / Forward-send roles (existing) ───────────────────
    BackToProjectIncharge: {
        roleNumber: 18,
        postType: 'Back_ProjectIncharge',
        modalTitle: 'Back to Project Incharge',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Collector: {
        roleNumber: 1,
        postType: 'SamanvayFundSection11',
        modalTitle: 'Send to Collector',
        userLabel: 'Select Collector',
        userDisplayMode: 'dropdown',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Samanvay: {
        roleNumber: 6,
        postType: 'ProjectInchargeFundSection11',
        modalTitle: 'Send to Samanvay Officer',
        userLabel: 'Select Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    LAO: {
        roleNumber: 3,
        postType: 'CollectorFundSection11',
        modalTitle: 'Assign LAO Officer',
        userLabel: 'Select LAO Officer',
        userDisplayMode: 'dropdown',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    BackToSamanvay: {
        roleNumber: 6,
        postType: 'Samanvay',
        modalTitle: 'Back to Samanvay',
        userLabel: 'Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAllUserRoleSamanvay',
        postFn: 'named_postTrackingSamanvay',   // legacy: apiService.postTrackingSamanvay()
    },

    // ─── Back_* roles — sabhi POST_TRACKING?type=<postType> use karte hain ──

    // Prathmik Adhisuchna (Section 11)
    Back_Samanvay: {
        roleNumber: 6,
        postType: 'Back_Samanvay',
        modalTitle: 'Send Back to Project Incharge',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAllUserRoleSamanvay',
        postFn: 'default',
    },
    Back_Collector: {
        roleNumber: 1,
        postType: 'Back_Collector',
        modalTitle: 'Send Back to Samanvay',
        userLabel: 'Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAO: {
        roleNumber: 3,
        postType: 'Back_LAO',
        modalTitle: 'Send Back to LAO',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // Section 11
    Back_SamanvaySection11: {
        roleNumber: 6,
        postType: 'Back_SamanvaySection11',
        modalTitle: 'Send Back to Samanvay (Section 11)',
        userLabel: 'Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAllUserRoleSamanvay',
        postFn: 'default',
    },
    Back_CollectorSection11: {
        roleNumber: 1,
        postType: 'Back_CollectorSection11',
        modalTitle: 'Send Back to Collector (Section 11)',
        userLabel: 'Collector',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOPublish: {
        roleNumber: 3,
        postType: 'Back_LAOPublish',
        modalTitle: 'Send Back to LAO',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_ProjectInchargeFundSection11: {
        roleNumber: 18,
        postType: 'Back_ProjectInchargeFundSection11',
        modalTitle: 'Send Back to Project Incharge (Fund Section 11)',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOFundSection11: {
        roleNumber: 3,
        postType: 'Back_LAOFundSection11',
        modalTitle: 'Send Back to LAO (Fund Section 11)',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_objection: {
        roleNumber: 3,
        postType: 'Back_objection',
        modalTitle: 'Send Back to LAO',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignLAO',
        postFn: 'default',
    },
    // Extend Date
    Back_SamanvayExtendDate: {
        roleNumber: 6,
        postType: 'Back_SamanvayExtendDate',
        modalTitle: 'Send Back to Samanvay',
        userLabel: 'Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAllUserRoleSamanvay',
        postFn: 'default',
    },
    Back_CollectorExtendDate: {
        roleNumber: 1,
        postType: 'Back_CollectorExtendDate',
        modalTitle: 'Send Back to Collector (Extend Date)',
        userLabel: 'Collector',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOExtendDate: {
        roleNumber: 3,
        postType: 'Back_LAOExtendDate',
        modalTitle: 'Send Back to LAO (Extend Date)',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // Report / Survey
    Back_TehsildarReport: {
        roleNumber: 5,                          // Tehsildar role = 5
        postType: 'Back_TehsildarReport',
        modalTitle: 'Send Back to Tehsildar (Report)',
        userLabel: 'Tehsildar',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_SLR: {
        roleNumber: 15,                          // TODO: confirm SLR roleNumber
        postType: 'Back_SLR',
        modalTitle: 'Send Back to DSLR',
        userLabel: 'DSLR Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_Surveyor: {
        roleNumber: 7,                          // TODO: confirm Surveyor roleNumber
        postType: 'Back_Surveyor',
        modalTitle: 'Send Back to Surveyor',
        userLabel: 'Surveyor',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_SLRV: {
        roleNumber: 15,                          // TODO: confirm SLRV roleNumber
        postType: 'Back_SLRV',
        modalTitle: 'Send Back to DSLR',
        userLabel: 'DSLR Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOV: {
        roleNumber: 3,                          // LAO Verification → LAO role
        postType: 'Back_LAOV',
        modalTitle: 'Send Back to LAO',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignLAO',
        postFn: 'default',
    },

    // Section 19
    Back_SamanvaySection19: {
        roleNumber: 6,
        postType: 'Back_SamanvaySection19',
        modalTitle: 'Send Back to Samanvay',
        userLabel: 'Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAllUserRoleSamanvay',
        postFn: 'default',
    },
    Back_CollectorSection19: {
        roleNumber: 1,
        postType: 'Back_CollectorSection19',
        modalTitle: 'Send Back to Collector ',
        userLabel: 'Collector',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOPublishSection19: {
        roleNumber: 3,
        postType: 'Back_LAOPublishSection19',
        modalTitle: 'Send Back to LAO (Publish Section 19)',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_ProjectInchargeFundSection19: {
        roleNumber: 18,
        postType: 'Back_ProjectInchargeFundSection19',
        modalTitle: 'Send Back to Project Incharge (Fund Section 19)',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOFundSection19: {
        roleNumber: 3,
        postType: 'Back_LAOFundSection19',
        modalTitle: 'Send Back to LAO (Fund Section 19)',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // Agency
    Back_AgricultureAgency: {
        roleNumber: 0,                          // TODO: confirm Agriculture Agency roleNumber
        postType: 'Back_AgricultureAgency',
        modalTitle: 'Send Back to Agriculture Agency',
        userLabel: 'Agriculture Agency Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_TPAgency: {
        roleNumber: 0,                          // TODO: confirm TP Agency roleNumber
        postType: 'Back_TPAgency',
        modalTitle: 'Send Back to TP Agency',
        userLabel: 'TP Agency Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_ForestAgency: {
        roleNumber: 0,                          // TODO: confirm Forest Agency roleNumber
        postType: 'Back_ForestAgency',
        modalTitle: 'Send Back to Forest Agency',
        userLabel: 'Forest Agency Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_Compensation: {
        roleNumber: 3,                          // Compensation typically under LAO
        postType: 'Back_Compensation',
        modalTitle: 'Send Back to Compensation',
        userLabel: 'Compensation Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // Section 23
    Back_SamanvaySection23: {
        roleNumber: 6,
        postType: 'Back_SamanvaySection23',
        modalTitle: 'Send Back to Samanvay',
        userLabel: 'Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAllUserRoleSamanvay',
        postFn: 'default',
    },
    Back_CollectorSection23: {
        roleNumber: 1,
        postType: 'Back_CollectorSection23',
        modalTitle: 'Send Back to Collector (Section 23)',
        userLabel: 'Collector',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOSection23: {
        roleNumber: 3,
        postType: 'Back_LAOSection23',
        modalTitle: 'Send Back to LAO',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // KJP
    Back_DSLRKJP: {
        roleNumber: 0,                          // TODO: confirm DSLR roleNumber
        postType: 'Back_DSLRKJP',
        modalTitle: 'Send Back to DSLR (KJP)',
        userLabel: 'DSLR Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_TehsildarKJP: {
        roleNumber: 5,
        postType: 'Back_TehsildarKJP',
        modalTitle: 'Send Back to Tehsildar (KJP)',
        userLabel: 'Tehsildar',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LAOKJP: {
        roleNumber: 3,
        postType: 'Back_LAOKJP',
        modalTitle: 'Send Back to LAO (KJP)',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_Payment: {
        roleNumber: 0,                          // TODO: confirm Payment roleNumber
        postType: 'Back_Payment',
        modalTitle: 'Send Back to Payment',
        userLabel: 'Payment Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // Objection
    Back_ObjectionSamanvay: {
        roleNumber: 6,
        postType: 'Back_ObjectionSamanvay',
        modalTitle: 'Send Back to Samanvay',
        userLabel: 'Samanvay Officer',
        userDisplayMode: 'name',
        apiFn: 'getAllUserRoleSamanvay',
        postFn: 'default',
    },
    Back_ObjectionCollector: {
        roleNumber: 1,
        postType: 'Back_ObjectionCollector',
        modalTitle: 'Send Back to Collector (Objection)',
        userLabel: 'Collector',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_ObjectionLAO: {
        roleNumber: 3,
        postType: 'Back_ObjectionLAO',
        modalTitle: 'Send Back to LAO (Objection)',
        userLabel: 'LAO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // Land
    Back_LandDRO: {
        roleNumber: 0,                          // TODO: confirm DRO roleNumber
        postType: 'Back_LandDRO',
        modalTitle: 'Send Back to DRO (Land)',
        userLabel: 'DRO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LandParuptProjectIncharge: {
        roleNumber: 18,
        postType: 'Back_LandParuptProjectIncharge',
        modalTitle: 'Send Back to Project Incharge (Land Parupt)',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LandParuptTehsildar: {
        roleNumber: 5,
        postType: 'Back_LandParuptTehsildar',
        modalTitle: 'Send Back to Tehsildar (Land Parupt)',
        userLabel: 'Tehsildar',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LandObjectionProjectIncharge: {
        roleNumber: 18,
        postType: 'Back_LandObjectionProjectIncharge',
        modalTitle: 'Send Back to Project Incharge (Land Objection)',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LandObjectionTehsildar: {
        roleNumber: 5,
        postType: 'Back_LandObjectionTehsildar',
        modalTitle: 'Send Back to Tehsildar (Land Objection)',
        userLabel: 'Tehsildar',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LandFinalProjectIncharge: {
        roleNumber: 18,
        postType: 'Back_LandFinalProjectIncharge',
        modalTitle: 'Send Back to Project Incharge (Land Final)',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_LandFinalTehsildar: {
        roleNumber: 5,
        postType: 'Back_LandFinalTehsildar',
        modalTitle: 'Send Back to Tehsildar (Land Final)',
        userLabel: 'Tehsildar',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_TabapavtiProjectIncharge: {
        roleNumber: 18,
        postType: 'Back_TabapavtiProjectIncharge',
        modalTitle: 'Send Back to Project Incharge (Tabapavti)',
        userLabel: 'Project Incharge',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
    Back_TabapavtiTehsildar: {
        roleNumber: 5,
        postType: 'Back_TabapavtiTehsildar',
        modalTitle: 'Send Back to Tehsildar (Tabapavti)',
        userLabel: 'Tehsildar',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },

    // Namuna
    Back_NamunaADRO: {
        roleNumber: 0,                          // TODO: confirm ADRO roleNumber
        postType: 'Back_NamunaADRO',
        modalTitle: 'Send Back to ADRO (Namuna)',
        userLabel: 'ADRO Officer',
        userDisplayMode: 'name',
        apiFn: 'getAssignUserRoleProjectIncharge',
        postFn: 'default',
    },
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const SendTrackingModal = ({
    isOpen,
    onClose,
    role,                  // e.g. "BackToProjectIncharge"
    projectId,
    proposalId,
    translations,
    language,
    onSuccess,
    overrideName,     // optional: specific user ka naam seedha pass karo
    overrideUserId,
}) => {
    const config = ROLE_CONFIG[role] || {};

    // ── State ──
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [projectInchargeName, setProjectInchargeName] = useState('');  // for display only
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [remarks, setRemarks] = useState('Approved');
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [documentUrl, setDocumentUrl] = useState('');
    const [showDocViewer, setShowDocViewer] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Digital Signature
    const { uploadStatus, uploadError, selectedSignDocument, resetSignatureState, signDocument } = useSignature();
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [pdfForSigning, setPdfForSigning] = useState(null);

    // ── Fetch users on open ──
    // ── Fetch users on open ──
    useEffect(() => {
        if (!isOpen || !projectId || !proposalId) return;

        // Reset state every time modal opens
        setRemarks('Approved');
        setSelectedDocument(null);
        resetSignatureState?.();

        // ✅ Override: parent ne naam/id diya ho to API call mat karo
        if (overrideUserId && overrideName) {
            setSelectedUserId(overrideUserId);
            setProjectInchargeName(overrideName);
            return;
        }

        fetchUsers();
        setSelectedUserId('');
        setProjectInchargeName('');
    }, [isOpen, projectId, proposalId, overrideUserId, overrideName]);
    // ── GET_ALL_USER API call ──
    // apiFn → kaunsa GET API call hoga
    // userDisplayMode='name'     → pehle user ka naam display + auto-set selectedUserId
    // userDisplayMode='dropdown' → dropdown mein sab users + pehla auto-select
    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            let response;
            if (config.apiFn === 'getAllUserRoleSamanvay') {
                // BackToSamanvay: role=6
                response = await apiService.getAllUserRoleSamanvay(
                    config.roleNumber, projectId, proposalId
                );
            } else if (config.apiFn === 'getAssignLAO') {
                response = await apiService.getAssignLAO(
                    config.roleNumber, projectId, proposalId
                );
            } else {
                // BackToProjectIncharge + all others: role=18/1/3/6
                response = await apiService.getAssignUserRoleProjectIncharge(
                    config.roleNumber, projectId, proposalId
                );
            }

            if (response.status === 200 && Array.isArray(response.data)) {
                setUsers(response.data);
                if (response.data.length > 0) {
                    const first = response.data[0];
                    // Auto-set first user's id (used in both name & dropdown mode)
                    setSelectedUserId(first.usr_id || first.id);
                    // For 'name' mode: save display name
                    if (config.userDisplayMode === 'name') {
                        setProjectInchargeName(first.full_name || first.user_name || config.userLabel);
                    }
                }
            }
        } catch (error) {
            console.error(`Error fetching users for role ${config.roleNumber}:`, error);
        } finally {
            setLoadingUsers(false);
        }
    };

    // ── Final document (signed > uploaded) ──
    const getFinalDocument = () => {
        if (selectedSignDocument) return selectedSignDocument;
        if (selectedDocument) return selectedDocument;
        return null;
    };

    // ── Validation ──
    const isValid = () => {
        const doc = getFinalDocument();
        if (!doc) return false;
        if (!selectedUserId) return false;
        return true;
    };

    // ── POST_TRACKING API call ──
    const handleSend = async () => {
        const finalDoc = getFinalDocument();
        if (!finalDoc) {
            alert('Please upload or sign a document');
            return;
        }
        if (!selectedUserId) {
            alert('User not found. Please try again.');
            return;
        }

        setIsSending(true);
        setShowProgress(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('project_id', projectId);
            formData.append('proposal_id', proposalId);
            formData.append('remarks', remarks);
            formData.append('usr_id', selectedUserId);
            formData.append('document', finalDoc);

            // POST routing via postFn:
            // Back_Samanvay → BackToProjectIncharge
            // postTrackingSamanvay              → BackToSamanvay
            // default                           → POST_TRACKING?type=...
            let response;
            if (config.postFn === 'named_BackSamanvay') {
                response = await apiService.Back_Samanvay(formData);
            } else if (config.postFn === 'named_postTrackingSamanvay') {
                response = await apiService.postTrackingSamanvay(formData, config.postType);
            } else {
                // default: sabhi Back_* roles — POST_TRACKING?type=Back_Samanvay / Back_Collector etc.
                const url = `${apiService.endpoints.POST_TRACKING}?type=${config.postType}`;
                response = await apiService.post(url, formData);
            }

            if (response.status === 200) {
                setUploadProgress(100);
                await new Promise(r => setTimeout(r, 400));
                onSuccess?.();   // refresh button status etc.
                handleClose();
            } else {
                setUploadProgress(0);
                alert('Failed to send. Please try again.');
            }
        } catch (error) {
            setUploadProgress(0);
            console.error('SendTrackingModal error:', error);
            alert('Something went wrong. Please check your connection.');
        } finally {
            setIsSending(false);
            setShowProgress(false);
            setUploadProgress(0);
        }
    };

    // ── Close & reset ──
    const handleClose = () => {
        setRemarks('Approved');
        setSelectedDocument(null);
        setSelectedUserId('');
        setProjectInchargeName('');
        setShowProgress(false);
        setUploadProgress(0);
        resetSignatureState?.();
        onClose();
    };

    // ── View document ──
    const handleViewDocument = (file) => {
        const url = URL.createObjectURL(file);
        setDocumentUrl(url);
        setShowDocViewer(true);
    };

    if (!isOpen) return null;

    return (
        <>
            {/* ── Main Modal ── */}
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="p-6">

                        {/* ── Header ── */}
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {config.modalTitle}
                            </h3>
                            <button
                                onClick={handleClose}
                                disabled={isSending}
                                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-4">

                            {/* ── User Section ── */}
                            {config.userDisplayMode === 'name' ? (
                                /* Back to Project Incharge: naam display karo, dropdown nahi */
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs text-blue-500 font-medium mb-1">
                                        {config.userLabel}
                                    </p>
                                    {loadingUsers ? (
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Loading...
                                        </div>
                                    ) : (
                                        <p className="text-sm font-semibold text-blue-800">
                                            {projectInchargeName || '—'}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                /* Other roles: dropdown */
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {config.userLabel} *
                                    </label>
                                    <select
                                        value={selectedUserId}
                                        onChange={e => setSelectedUserId(e.target.value)}
                                        disabled={loadingUsers}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">
                                            {loadingUsers ? 'Loading...' : `Select ${config.userLabel}`}
                                        </option>
                                        {users.map(u => (
                                            <option key={u.usr_id || u.id} value={u.usr_id || u.id}>
                                                {u.full_name || u.user_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* ── Remarks ── */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translations[language]?.Remarks}
                                </label>
                                <textarea
                                    value={remarks}
                                    onChange={e => setRemarks(e.target.value)}
                                    rows={3}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={translations[language]?.EnterRemarks}
                                />
                            </div>

                            {/* ── Upload Document ── */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translations[language]?.UploadDocument} *
                                </label>
                                <input
                                    type="file"
                                    onChange={e => setSelectedDocument(e.target.files[0])}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />

                                {/* Selected document info */}
                                {selectedDocument && (
                                    <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm text-green-700 font-medium truncate max-w-[60%]">
                                                {translations[language]?.Selected}: {selectedDocument.name}
                                            </p>
                                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                                {(selectedDocument.size / 1024 / 1024).toFixed(2)} MB
                                            </span>
                                        </div>

                                        {/* Sign + View buttons (PDF only) */}
                                        {selectedDocument.type === 'application/pdf' && (
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setPdfForSigning(selectedDocument);
                                                        setShowSignaturePad(true);
                                                    }}
                                                    disabled={uploadStatus === 'uploading'}
                                                    className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                                                >
                                                    {uploadStatus === 'uploading' ? (
                                                        <>
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                            Signing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                            </svg>
                                                            Sign Doc
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewDocument(
                                                        uploadStatus === 'success' ? selectedSignDocument : selectedDocument
                                                    )}
                                                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 transition-colors"
                                                >
                                                    <Eye className="h-3 w-3" />
                                                    {translations[language]?.View}
                                                </button>
                                            </div>
                                        )}

                                        {/* Signature status */}
                                        {uploadStatus === 'success' && (
                                            <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs text-green-700">
                                                ✅ Document Signed Successfully
                                            </div>
                                        )}
                                        {uploadStatus === 'error' && uploadError && (
                                            <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                                                ❌ {uploadError}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                        </div>

                        {/* ── Footer Buttons ── */}
                        <div className="flex flex-col gap-4 mt-6 pt-4 border-t">
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={handleClose}
                                    disabled={isSending || showProgress}
                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    {translations[language]?.Cancel}
                                </button>
                                <button
                                    onClick={handleSend}
                                    disabled={!isValid() || isSending || showProgress}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                                >
                                    {isSending || showProgress ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {showProgress ? translations[language]?.Sending : translations[language]?.Send}
                                        </>
                                    ) : (
                                        <>
                                            <Send className="mr-2 h-4 w-4" />
                                            {translations[language]?.Send}
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Progress Bar */}
                            {showProgress && (
                                <div className="w-full">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>{translations[language]?.sending}</span>
                                        <span>{uploadProgress}%</span>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Signature Pad ── */}
                {showSignaturePad && pdfForSigning && (
                    <SignaturePad
                        pdfFile={pdfForSigning}
                        onSign={(signaturesArray) => {
                            setShowSignaturePad(false);
                            signDocument(pdfForSigning, signaturesArray).then(() => {
                                setPdfForSigning(null);
                            });
                        }}
                        onCancel={() => {
                            setShowSignaturePad(false);
                            setPdfForSigning(null);
                        }}
                    />
                )}
            </div>

            {/* ── Inline Document Preview ── */}
            {showDocViewer && documentUrl && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {translations[language]?.DocumentPreview}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowDocViewer(false);
                                    URL.revokeObjectURL(documentUrl);
                                }}
                                className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>
                        <iframe
                            src={documentUrl}
                            className="w-full h-[80vh] rounded border border-gray-300"
                            title="Document Preview"
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default SendTrackingModal;